import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import { doNothing } from 'remeda'

type RegisterProps = {
    id: string
    setVisible(visible: boolean): void
}

type InternalRef = {
    lastOpened: string | null
    callbacks: Record<string, (visible: boolean) => void>
}

type Context = {
    register(props: RegisterProps): VoidFunction
    toggle(id: string): void
}

const TooltipInternalContext = createContext<Context>({
    register: () => doNothing,
    toggle: doNothing,
})

export const TooltipInternalProvider = ({ children }: { children: ReactNode }) => {
    const ref = useRef<InternalRef>({
        lastOpened: null,
        callbacks: {},
    })

    const register = useCallback(({ id, setVisible }: RegisterProps) => {
        ref.current.callbacks[id] ??= setVisible

        return () => {
            const { [id]: ignored, ...rest } = ref.current.callbacks

            ref.current.callbacks = rest
        }
    }, [])

    const toggle = useCallback((id: string) => {
        const { callbacks, lastOpened } = ref.current

        if (callbacks[id] && lastOpened !== id) {
            if (lastOpened) {
                callbacks[lastOpened]?.(false)
            }

            ref.current.lastOpened = id
        }
    }, [])

    const providerValue = useMemo(
        () => ({
            register,
            toggle,
        }),
        [register, toggle],
    )

    return <TooltipInternalContext.Provider value={providerValue}>{children}</TooltipInternalContext.Provider>
}

export const useTooltipInternalProvider = ({ id, setVisible }: RegisterProps) => {
    const { register, toggle } = useContext(TooltipInternalContext)

    useEffect(() => register({ id, setVisible }), [id, setVisible, register])

    return useCallback(() => toggle(id), [toggle, id])
}
